import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@patient-watch/ui/form";
import { Label } from "@patient-watch/ui/label";
import { Card, cn, RadioGroup, RadioGroupItem } from "@patient-watch/ui";
import type { InputT } from "@patient-watch/types";
import { useFormContext } from "react-hook-form";
import { RichLabel } from "./components/RichLabel";

interface Props {
    input: InputT;
}

function normalizeOptions(input: InputT): { value: string; label: string }[] {
    if (input.scale_options?.length) {
        return input.scale_options.map((o) => ({
            value: String(o.value),
            label: o.label,
        }));
    }
    return (input.options ?? []).map((o) => ({ value: o, label: o }));
}

const SingleChoice = ({ input }: Props) => {
    const { control } = useFormContext();
    const options = normalizeOptions(input);

    return (
        <FormField
            control={control}
            name={input.input_id}
            render={({ field }) => (
                <FormItem>
                    <Card className="flex flex-col gap-2 bg-white/70 p-4">
                        <FormLabel>
                            {input.input_caption && (
                                <span className="block mb-1 font-normal text-muted-foreground text-sm italic">
                                    {input.input_caption}
                                </span>
                            )}
                            <Label>
                                <RichLabel>{input.input_label}</RichLabel>
                            </Label>
                            {!input.is_optional && <span className="ml-0.5 text-red-500">*</span>}
                        </FormLabel>
                        {input.input_description && (
                            <FormDescription>
                                <RichLabel>{input.input_description}</RichLabel>
                            </FormDescription>
                        )}
                        <FormControl>
                            <RadioGroup
                                onValueChange={field.onChange}
                                value={field.value}
                                className="flex flex-col gap-y-2"
                                data-testid={`form-options-${input.input_id}`}
                            >
                                {options.map((o) => {
                                    const isSelected = field.value === o.value;
                                    return (
                                        <label
                                            className={cn(
                                                "flex justify-start items-center gap-2 py-1 pl-2 rounded-md transition-[box-shadow] duration-200 cursor-pointer",
                                                isSelected && "ring-2 ring-indigo-600 dark:ring-indigo-400"
                                            )}
                                            key={o.value}
                                            htmlFor={`${input.input_id}-${o.value}`}
                                            data-testid="form-option"
                                            data-input-id={input.input_id}
                                            data-option-value={o.value}
                                        >
                                            <RadioGroupItem
                                                value={o.value}
                                                id={`${input.input_id}-${o.value}`}
                                                className="flex-shrink-0 min-w-[2.5rem] min-h-[2.5rem] shrink-0"
                                                data-testid={`form-option-${input.input_id}-${o.value}`}
                                            />
                                            <span className="py-2.5 w-full text-sm leading-snug">{o.label}</span>
                                        </label>
                                    );
                                })}
                            </RadioGroup>
                        </FormControl>
                        <FormMessage />
                    </Card>
                </FormItem>
            )}
        />
    );
};

export default SingleChoice;
